/** Ordre significatif : la première règle qui correspond l'emporte. */
const REGLES = [
    {
        // 4 numéros sur 4 en base, tous en `FRT-` suivi de chiffres.
        motif: /^FRT-\d{4,}$/i,
        transporteur: 'DHL',
        confiance: 'certaine',
        explication: 'numéro en FRT-',
    },
    {
        /*
         * Seconde forme DHL, à 11 chiffres : `00063570394`, `00063571209`,
         * `00063583107`, signalés par Mosaik.
         *
         * Les trois partagent le préfixe `000635`, et la règle ne s'y appuie
         * PAS — même raisonnement que DPD plus bas : trois numéros ne suffisent
         * pas à affirmer qu'aucun envoi DHL ne s'en écarte, et ce préfixe est
         * vraisemblablement celui du compte expéditeur, qui changerait avec le
         * contrat.
         *
         * La longueur ne chevauche aucune autre règle connue — 12 pour FedEx, 14
         * pour DPD, 18 pour DB Schenker, 20 pour Dachser —, mais reste
         * `probable` comme toutes les règles fondées sur la seule longueur : un
         * transporteur encore inconnu de Mosaik pourrait employer le même format.
         * C'est aussi ce qui garantit qu'un transporteur saisi à la main n'est
         * jamais écrasé par cette détection.
         */
        motif: /^\d{11}$/,
        transporteur: 'DHL',
        confiance: 'probable',
        explication: '11 chiffres',
    },
    {
        // Un seul numéro en base (`876620823214`), rattaché à FedEx sur indication
        // de Mosaik : la règle tient sur la longueur, pas sur un préfixe. Même
        // maillon faible que DB Schenker ci-dessous, d'où `probable`.
        motif: /^\d{12}$/,
        transporteur: 'FedEx',
        confiance: 'probable',
        explication: '12 chiffres',
    },
    {
        // 20 numéros sur 20 en base, tous ouverts par le préfixe GS1 de Dachser.
        motif: /^0034032685\d{10}$/,
        transporteur: 'Dachser',
        confiance: 'certaine',
        explication: '20 chiffres au préfixe Dachser',
    },
    {
        // Même forme, préfixe d'entreprise différent : c'est un SSCC, donc très
        // probablement Dachser chez Mosaik, mais le préfixe ne le confirme pas.
        motif: /^00\d{18}$/,
        transporteur: 'Dachser',
        confiance: 'probable',
        explication: 'format SSCC à 20 chiffres',
    },
    {
        // `FRLYS590417925` : « FR » + le code de l'agence (LYS = Lyon) + le numéro.
        // Identifié par Mosaik ; un seul exemplaire en base, d'où `probable`.
        // Référence DB Schenker, aujourd'hui DSV (fusion de 2026).
        motif: /^FR[A-Z]{3}\d{6,}$/i,
        transporteur: 'DSV',
        confiance: 'probable',
        explication: 'référence en FR + code agence',
    },
    {
        // Un seul numéro en base : la règle tient sur la longueur, pas sur un
        // préfixe. À confirmer quand d'autres envois DSV arriveront.
        motif: /^\d{18}$/,
        transporteur: 'DSV',
        confiance: 'probable',
        explication: '18 chiffres',
    },
    {
        /*
         * Deux numéros en base, tous deux confirmés DPD par Mosaik :
         * `10594007961888` et `10594007852581`. Ils partagent le préfixe
         * `10594007` — vraisemblablement celui du compte expéditeur —, mais la
         * règle tient sur la LONGUEUR, pas sur ce préfixe : deux numéros ne
         * suffisent pas à affirmer qu'aucun envoi DPD ne s'en écarte.
         *
         * D'où `probable`, comme FedEx : la longueur seule ne distingue pas.
         *
         * Cette règle a été retirée un temps au profit de Rhenus, sur une
         * attribution erronée de `10594007852581`. Mosaik a rectifié : c'est bien
         * du DPD. On la rétablit — avec un exemplaire de plus qu'avant.
         */
        motif: /^\d{14}$/,
        transporteur: 'DPD',
        confiance: 'probable',
        explication: '14 chiffres',
    },
];
/**
 * Forme comparable d'un numéro : sans espaces, y compris à l'intérieur. La base
 * en contient au moins un saisi par groupes (`8766208232 14`), et ces espaces
 * ne font pas partie du numéro — ni pour le reconnaître, ni dans l'URL de
 * suivi, où ils donneraient un lien mort.
 */
function normaliserTracking(tracking) {
    return String(tracking ?? '').trim().replace(/\s+/g, '');
}
/**
 * Devine le transporteur d'un numéro de suivi, ou `null` si aucun format connu
 * ne correspond — on ne propose alors rien plutôt qu'un transporteur au hasard.
 */
export function transporteurDepuisTracking(tracking) {
    const t = normaliserTracking(tracking);
    if (!t)
        return null;
    for (const regle of REGLES) {
        if (regle.motif.test(t)) {
            return { transporteur: regle.transporteur, confiance: regle.confiance, motif: regle.explication };
        }
    }
    return null;
}
/**
 * Le nom ACTUEL d'un transporteur.
 *
 * DB Schenker a fusionné avec DSV en 2026 : ses interlocuteurs et son suivi
 * sont désormais ceux de DSV. Tout libellé qui dit « Schenker » — saisi avant la
 * fusion, ou repris d'un mail — se lit donc « DSV ». Les autres sont rendus tels
 * quels ; `null` pour un libellé vide.
 */
export function nomTransporteur(nom) {
    const n = String(nom ?? '').trim();
    if (!n)
        return null;
    return /schenker/i.test(n) ? 'DSV' : n;
}
/**
 * Construction des URL de suivi transporteur.
 *
 * Formats relevés dans le prototype. Un transporteur sans page connue renvoie
 * `null` : l'interface affiche alors un bouton désactivé avec l'explication au
 * survol — jamais un bouton absent, jamais un lien mort.
 */
export function urlSuiviTransporteur(transporteur, tracking, 
/**
 * Code postal du destinataire, quand la commande le porte.
 *
 * Seul Rhenus en a besoin pour son LIEN : sa page ne rend rien sans lui. Les
 * autres transporteurs l'ignorent — DPD l'exige pour son relevé, mais pas
 * pour ouvrir sa page. Facultatif, et le lien reste utile sans : la page
 * Rhenus demande alors le code postal à l'écran.
 */
codePostal) {
    const numero = normaliserTracking(tracking);
    if (!numero)
        return null;
    const t = encodeURIComponent(numero);
    // Trois cas, dans cet ordre :
    //
    // 1. **le format est certain** — il prime sur le transporteur saisi : un
    //    numéro en `FRT-` est un envoi DHL, même si la commande dit autre chose ;
    // 2. **un transporteur est saisi** — on le croit ;
    // 3. **rien n'est saisi, mais le format est reconnaissable** — on se sert de
    //    la reconnaissance, même « probable ».
    //
    // Le troisième cas manquait, et il coûtait un lien : `#MF3366` porte le
    // numéro FedEx `876620823214` avec un champ transporteur VIDE. La
    // reconnaissance par longueur le donne « probable », ce qui ne suffisait pas —
    // la commande n'avait donc aucun bouton de suivi, et l'infobulle annonçait
    // « transporteur sans page de suivi connue » alors que la page existe et que
    // le colis y était marqué livré depuis le 08/09.
    //
    // Une reconnaissance probable vaut mieux que rien : au pire le lien ne trouve
    // pas l'envoi, ce qui se voit en un clic — alors qu'un bouton absent ne se
    // diagnostique pas.
    const detecte = transporteurDepuisTracking(tracking);
    const effectif = detecte?.confiance === 'certaine'
        ? detecte.transporteur
        : (nomTransporteur(transporteur) || detecte?.transporteur || null);
    switch (effectif) {
        case 'Dachser':
            return `https://elogistics.dachser.com/shp2s/?search=${t}&javalocale=fr_FR`;
        case 'DSV':
            // Sans `refType` : DSV cherche alors sur tous les types de référence —
            // numéro de colis comme numéro d'expédition. Même URL que le relevé.
            return `https://www.dsv.com/mydsv/tracking-public/?refNumber=${t}`;
        case 'DHL':
            return `https://www.dhl.com/fr-fr/home/tracking.html?tracking-id=${t}`;
        case 'FedEx':
            // Le numéro SEUL suffit : vérifié le 18/09/2026 sur `876620823214`.
            //
            // Les liens que FedEx distribue portent un `trkqual` en plus —
            // `2461286000~876620823214~FX` —, un qualificatif qui lève l'ambiguïté
            // quand un numéro à 12 chiffres a été recyclé. Il se lit
            // `<identifiant FedEx>~<numéro>~<filiale>` (`FX` = Express, `FDEG` =
            // Ground, `FDEF` = Freight), et son premier champ est FABRIQUÉ PAR FEDEX :
            // il ne se déduit pas du numéro, donc il ne se généralise pas.
            //
            // Il n'y en a pas besoin : ouvrir `?trknbr=` seul fait rediriger FedEx
            // vers l'URL complète, qualificatif compris. C'est lui qui le calcule.
            return `https://www.fedex.com/fedextrack/?trknbr=${t}`;
        case 'DPD':
            return `https://www.dpd.fr/trace/${t}`;
        case 'Rhenus': {
            /*
             * Relevé sur le site le 18/09/2026.
             *
             * Le code postal du destinataire est un SECOND FACTEUR, pas un confort :
             * l'API répond 404 sans lui, et la page affiche « Enter zip code ». On
             * l'ajoute donc quand la commande le porte ; sans lui le lien reste utile,
             * la page réclamant le code à l'écran plutôt que de ne rien rendre.
             */
            const cp = String(codePostal ?? '').trim();
            return 'https://trackandtrace.roadit.rhenus.com/info/'
                + `?referenceNumber=${t}${cp ? `&zipCode=${encodeURIComponent(cp)}` : ''}`;
        }
        default:
            return null;
    }
}
/**
 * Raison affichée au survol quand le bouton de suivi est désactivé.
 *
 * Elle nomme le transporteur **effectivement retenu**, pas celui qui est saisi :
 * une reconnaissance certaine prime sur la saisie, et accuser un transporteur
 * de n'avoir pas de page de suivi quand ce n'est pas lui qui transporte serait
 * faux deux fois.
 *
 * (Cette note citait `#BF1139` comme « un Rhenus reconnu avec certitude ».
 * C'était faux : Mosaik a rectifié, son numéro est un DPD, et la règle Rhenus
 * a été retirée faute d'un seul exemple réel — cf. l'en-tête de ce fichier.)
 */
export function raisonSuiviIndisponible(c) {
    if (urlSuiviTransporteur(c.transporteur, c.tracking))
        return null;
    if (!c.tracking)
        return "Pas encore de numéro de suivi";
    const detecte = transporteurDepuisTracking(c.tracking);
    const effectif = detecte?.confiance === 'certaine'
        ? detecte.transporteur
        : (c.transporteur || detecte?.transporteur || null);
    // Sans transporteur saisi ET sans format reconnu, on ne sait honnêtement pas
    // où pointer — le dire ainsi, plutôt que d'accuser un transporteur d'être
    // dépourvu de page de suivi.
    if (!effectif) {
        return 'Transporteur non renseigné, et ce numéro ne ressemble à aucun format connu';
    }
    // Quand la reconnaissance a contredit la saisie, le dire : sans cela, on
    // chercherait pourquoi « DPD » n'a pas de lien alors qu'il en a un.
    const contredit = detecte?.confiance === 'certaine' && c.transporteur
        && c.transporteur !== detecte.transporteur;
    return `Transporteur sans page de suivi connue (${effectif}`
        + `${contredit ? `, reconnu d'après le ${detecte.motif}` : ''})`;
}
/** Le magasin Shopify est le nôtre : il ne change pas d'une commande à l'autre. */
const BOUTIQUE_SHOPIFY = 'mosaik-floor';
/**
 * Lien vers la commande dans son outil d'origine.
 *
 * Shopify d'abord, Sellsy à défaut : une commande Shopify facturée par Sellsy
 * porte les deux identifiants, et c'est la commande qu'on veut voir, pas la
 * facture qui en découle. Sans identifiant, `null` — la référence reste alors du
 * texte, plutôt qu'un lien qui ne mènerait nulle part.
 *
 * Les nombres sont acceptés autant que les chaînes : un identifiant Shopify est
 * numérique en base, et les lectures `lean()` le remontent tel quel, sans passer
 * par la conversion du schéma.
 */
export function urlBoutique(shopifyId, sellsyId) {
    const shopify = String(shopifyId ?? '').trim();
    if (shopify) {
        return {
            origine: 'shopify',
            url: `https://admin.shopify.com/store/${BOUTIQUE_SHOPIFY}/orders/${encodeURIComponent(shopify)}`,
        };
    }
    const sellsy = String(sellsyId ?? '').trim();
    if (sellsy) {
        return {
            origine: 'sellsy',
            url: `https://www.sellsy.com/?_f=invoiceOverview&id=${encodeURIComponent(sellsy)}`,
        };
    }
    return null;
}
//# sourceMappingURL=suivi.js.map